import { useEffect, useRef } from "react"

import { drawFaceMesh, drawFinalFaceMesh } from "./draw-face-landmarks"
import { FACE_MESH_LAYERS } from "./face-landmarks-config"
import type { FaceMeshCapture } from "./types"

export interface StaticFaceMeshProps {
  /** The captured detection to draw. Renders nothing when null. */
  capture: FaceMeshCapture | null
  className?: string
  /**
   * Mirror to match a mirrored selfie photo. When omitted, follows the capture's
   * own `mirrored` flag (selfie → true, gallery upload → false).
   */
  mirror?: boolean
  /**
   * Draw the full colored mesh (tesselation + red/green eyes, brows, lips, irises),
   * like the live camera, instead of the minimal settled tesselation + oval.
   */
  full?: boolean
}

/**
 * Draws a captured face mesh onto a canvas to overlay a still preview photo. By
 * default it's the settled mesh (tesselation + oval); pass `full` for the live
 * camera's colored mesh. Size the canvas with CSS (e.g. `absolute inset-0 size-full
 * object-cover`) to match the photo box.
 */
export function StaticFaceMesh({ capture, className, mirror, full = false }: StaticFaceMeshProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas || !capture) return
    canvas.width = capture.videoWidth
    canvas.height = capture.videoHeight
    const ctx = canvas.getContext("2d")
    if (!ctx) return
    const shouldMirror = mirror ?? capture.mirrored ?? true
    if (full) {
      drawFaceMesh(ctx, capture.results, canvas.width, canvas.height, {
        progress: 1,
        mirror: shouldMirror,
        layers: FACE_MESH_LAYERS,
      })
    } else {
      drawFinalFaceMesh(ctx, capture.results, canvas.width, canvas.height, shouldMirror)
    }
  }, [capture, mirror, full])

  if (!capture) return null
  return <canvas ref={canvasRef} className={className} aria-hidden />
}
